import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Label, RequiredMark } from '@/components/ui';
import {
  releaseScheduleCancelSchema,
  type ReleaseScheduleCancelValues,
} from '../schemas/settlementFormSchema';

export interface ReleaseCancelModalProps {
  isOpen: boolean;
  title: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: (values: ReleaseScheduleCancelValues) => void;
  onCancel: () => void;
}

export const ReleaseCancelModal = ({
  isOpen,
  title,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: ReleaseCancelModalProps) => {
  const { t } = useTranslation('settlement');
  const form = useForm<ReleaseScheduleCancelValues>({
    resolver: zodResolver(releaseScheduleCancelSchema),
    defaultValues: { cancelReason: '' },
    mode: 'onBlur',
  });

  const reasonError = form.formState.errors.cancelReason?.message as string | undefined;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="release-cancel-modal" className="max-w-md">
        <DialogTitle>{t('settlement.release.cancel.title')}</DialogTitle>
        <DialogDescription>
          {t('settlement.release.cancel.subject', { title })}
        </DialogDescription>

        <p
          className="rounded-md p-3 text-sm"
          style={{
            background: 'var(--status-warn-bg)',
            color: 'var(--status-warn-fg)',
          }}
        >
          {t('settlement.release.cancel.confirm')}
        </p>

        <form
          onSubmit={form.handleSubmit(onConfirm)}
          className="flex flex-col gap-2"
          noValidate
        >
          <Label htmlFor="cancel-reason">
            {t('settlement.release.cancel.reasonLabel')}
            <RequiredMark />
          </Label>
          <textarea
            id="cancel-reason"
            {...form.register('cancelReason')}
            maxLength={500}
            rows={3}
            placeholder={t('settlement.release.cancel.reasonPlaceholder')}
            className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
            style={{ borderColor: 'var(--content-muted)' }}
            aria-invalid={Boolean(reasonError)}
          />
          {reasonError ? (
            <span
              role="alert"
              className="text-xs"
              style={{ color: 'var(--signal-danger)' }}
            >
              {t(reasonError, { defaultValue: reasonError })}
            </span>
          ) : null}

          {errorMessage ? (
            <p
              role="alert"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('settlement.actions.cancel')}
            </Button>
            <Button type="submit" variant="danger" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('settlement.release.cancel.submitting')
                : t('settlement.release.cancel.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
